<script setup lang="ts">
import Simplebar from "simplebar-vue";
import { computed, ref, shallowRef, useTemplateRef } from "vue";
import { useRoute } from "vue-router";

import DropdownButton from "@/components/ui/DropdownButton.vue";
import DropdownCustomItem from "@/components/ui/DropdownCustomItem.vue";
import RichTextEditor from "@/components/ui/RichTextEditor.vue";
import SimpleButton from "@/components/ui/SimpleButton.vue";
import SkoreSpinner from "@/components/ui/SkoreSpinner.vue";
import { type PresentationDto, type PresentationItemDto } from "@/dto";
import { ProjectDetailSections, RouteNames } from "@/router";
import { useApi } from "@/services/api";
import { useModalsStore } from "@/stores/modals";
import { useToastsStore } from "@/stores/toasts";

const { name, report_id, comparison_id, media_type, value } = defineProps<
  Pick<PresentationItemDto, "name" | "media_type" | "value"> &
    Partial<Pick<PresentationItemDto, "report_id" | "comparison_id">>
>();

const api = useApi();
const route = useRoute();
const modals = useModalsStore();
const toasts = useToastsStore();

const presentations = ref<PresentationDto[]>([]);
const dropdown = useTemplateRef<InstanceType<typeof DropdownButton>>("dropdown");
const targetPresentationsIds = shallowRef<number[]>([]);
const isLoading = ref(true);
const isEditingNote = ref(false);
const richTextEditor = useTemplateRef<InstanceType<typeof RichTextEditor>>("richTextEditor");
const note = ref("");

const tenantName = computed(() => route.params.tenantName as string);
const projectName = computed(() => route.params.projectName as string);

async function fetchPresentationList() {
  isLoading.value = true;
  const list = await api.listProjectPresentations(tenantName.value, projectName.value);
  presentations.value = list.sort((a, b) => -a.updated_at.localeCompare(b.updated_at));
  isLoading.value = false;
}

async function sendToPresentation() {
  const newIds = [];
  for (const presentationId of targetPresentationsIds.value) {
    // create the item

    newIds.push(
      await api.createPresentationItem(
        tenantName.value,
        projectName.value,
        presentationId,
        name,
        note.value,
        media_type,
        value,
        {
          report_id: report_id ?? null,
          comparison_id: comparison_id ?? null,
        }
      )
    );
  }

  if (targetPresentationsIds.value.length > 0) {
    const plural = targetPresentationsIds.value.length > 1 ? "s" : "";
    if (!newIds.some((id) => id === -1)) {
      toasts.addToast(`Item successfully added to presentation${plural}.`, "success", {
        duration: 5,
        link: {
          label: "Go to presentation",
          to: {
            name: RouteNames.ProjectDetail,
            params: {
              tenantName: tenantName.value,
              projectName: projectName.value,
              section: ProjectDetailSections.Presentations,
              itemId: targetPresentationsIds.value[0],
            },
          },
        },
      });
    }
  }

  dropdown.value?.close();
  await reset(true);
}

function selectTargetPresentation(targetId: number) {
  if (targetPresentationsIds.value.includes(targetId)) {
    targetPresentationsIds.value = targetPresentationsIds.value.filter((id) => id !== targetId);
  } else {
    targetPresentationsIds.value = [...targetPresentationsIds.value, targetId];
  }
}

async function reset(full: boolean = false) {
  isEditingNote.value = false;
  if (full) {
    targetPresentationsIds.value = [];
  }
  await fetchPresentationList();
}

async function newPresentation() {
  const [confirmed, name] = await modals.prompt({
    title: "New presentation",
    message: "Give a name to your new presentation.",
  });
  if (confirmed && name) {
    const np = await api.createProjectPresentation(tenantName.value, projectName.value, name);
    if (np) {
      await fetchPresentationList();
      targetPresentationsIds.value = [...targetPresentationsIds.value, np.id];
      dropdown.value?.open();
    }
  }
}
</script>

<template>
  <span class="add-to-presentation-menu">
    <DropdownButton
      icon-right="icon-new-presentation"
      :allowed-placements="['bottom-start', 'bottom-end']"
      :highlight-when-open="true"
      ref="dropdown"
      @open="fetchPresentationList()"
      @close="reset()"
    >
      <DropdownCustomItem class="content">
        <div v-if="isLoading" class="loading">
          <SkoreSpinner />
        </div>
        <div v-else-if="!isEditingNote" class="selector">
          <div v-if="presentations.length === 0" class="placeholder">No presentation</div>
          <Simplebar v-else class="scroller">
            <div class="choices">
              <div
                v-for="p in presentations"
                :key="p.id"
                class="choice"
                :class="{ selected: targetPresentationsIds.includes(p.id) }"
                @click="selectTargetPresentation(p.id)"
              >
                <span>{{ p.name }}</span>
                <Transition name="fade">
                  <span v-if="targetPresentationsIds.includes(p.id)">
                    <i class="icon icon-check" />
                  </span>
                </Transition>
              </div>
            </div>
          </Simplebar>
          <div class="new" @click="newPresentation()">
            <span><i class="icon icon-plus-circle" /> Create a presentation</span>
          </div>
        </div>
        <div v-else class="note">
          <div class="heading">Description</div>
          <div class="editor">
            <div class="format-actions">
              <div class="format" @click="richTextEditor?.markBold()">
                <i class="icon icon-bold" />
              </div>
              <div class="format" @click="richTextEditor?.markItalic()">
                <i class="icon icon-italic" />
              </div>
              <div class="format" @click="richTextEditor?.markList()">
                <i class="icon icon-list" />
              </div>
            </div>
            <RichTextEditor ref="richTextEditor" v-model:value="note" />
          </div>
        </div>
        <div
          class="actions"
          :class="{ multiple: !isEditingNote, visible: targetPresentationsIds.length > 0 }"
        >
          <SimpleButton
            v-if="!isEditingNote"
            label="Add a note"
            icon-left="icon-sticky-note"
            kind="tertiary"
            :disabled="targetPresentationsIds.length === 0"
            @click.stop.prevent="isEditingNote = true"
          />
          <SimpleButton
            label="Add item"
            icon-left="icon-plus"
            kind="primary"
            :disabled="targetPresentationsIds.length === 0"
            @click.stop.prevent="sendToPresentation()"
          />
        </div>
      </DropdownCustomItem>
    </DropdownButton>
  </span>
</template>

<style scoped>
.add-to-presentation-menu {
  interpolate-size: allow-keywords;

  .loading {
    padding: var(--spacing-4);
    text-align: center;
  }

  .selector {
    .scroller {
      overflow: visible scroll;
      max-height: 25dvh;

      &:has(.choice) {
        padding: var(--spacing-4);
      }
    }

    .placeholder {
      padding: var(--spacing-8);
      color: var(--color-text-secondary);
    }

    .choices {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-4);

      .choice {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        padding: var(--spacing-8);
        border-radius: var(--radius-s);
        background-color: var(--color-background-primary);
        cursor: pointer;
        transition: background-color var(--animation-duration) var(--animation-easing);

        &:hover,
        &.selected {
          background-color: var(--color-background-secondary);
        }
      }
    }
  }

  .new {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: var(--spacing-8);
    border-radius: var(--radius-s);
    margin: var(--spacing-4);
    background-color: var(--color-background-primary);
    color: var(--color-text-branding);
    cursor: pointer;
    transition: background-color var(--animation-duration) var(--animation-easing);

    &:hover,
    &.selected {
      background-color: var(--color-background-secondary);
    }

    i {
      font-size: var(--font-size-md);
      vertical-align: text-bottom;
    }
  }

  .note {
    width: 300px;
    padding: var(--spacing-4);

    .heading {
      padding: var(--spacing-4);
      color: var(--color-text-secondary);
      font-size: var(--font-size-sm);
      font-weight: var(--font-weight-regular);
    }

    .editor {
      height: 15dvh;
      padding: var(--spacing-4);
      border-radius: var(--radius-s);
      background-color: var(--color-background-secondary);

      .format-actions {
        display: flex;
        flex-direction: row;
        justify-content: flex-end;
      }
    }
  }

  .actions {
    display: grid;
    overflow: hidden;
    height: 0;
    border-top: solid var(--stroke-width-m) var(--color-stroke-background-primary);
    gap: var(--spacing-4);
    grid-template-columns: 1fr;
    transition:
      height var(--animation-duration) var(--animation-easing),
      padding var(--animation-duration) var(--animation-easing);

    &.multiple {
      grid-template-columns: 1fr 1fr;
    }

    &.visible {
      height: auto;
      padding: var(--spacing-4);
    }
  }
}
</style>
